import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import Admin from 'admin';
import Party from 'model/party';
import Calendar from 'model/calendar';
import Political from 'model/political';
import clock from 'util/clock';
import { resetAmbient } from 'model/ambient';
import GameMap from 'lib/map';

import {
  onDayChange,
  onHistoryActionsChanged,
  onHistoryChanged,
  onLevelChanged,
  onResourceChanged,
  resetEvents
} from 'controllers/EventManager';
import { handleDayChange } from 'controllers/DailyUpdatesController';
import ResourceController from 'controllers/ResourceController';
import TerrainTracker from 'model/TerrainTracker';
import { ResourceTypes } from 'model/party/resources/resources';
import { healthBand, healthCrossing } from 'model/party/resources/health';
import Debug from 'debug';
import Locations from 'model/locations';
const debug = Debug('context.GameContext');

const GameContext = createContext();

const GameProvider = ({ children }) => {
  // START GAME location and phase
  const admin = Admin();
  const startLocation = admin.startLocation;
  const startPhase = admin.startPhase;
  const gracePeriod = admin.gracePeriod;

  let [gameState, setGameState] = useState({
    map: null,
    party: new Party(startPhase),
    calendar: new Calendar(),
    political: null,
    terrain: null,
    location: startLocation,
    message: '',
    position: 0,
    eventQueue: [],
    currentEvent: null,
    notifications: [],
    step: 0,
    lastStep: 0
  });

  const setEvent = useCallback(event => {
    setGameState(prev => {
      debug('setEvent', event, prev.currentEvent, [...prev.eventQueue, event]);
      if (!event) {
        return { ...prev, currentEvent: null };
      } else {
        return {
          ...prev,
          currentEvent: null,
          eventQueue: [...prev.eventQueue, event]
        };
      }
    });
  }, []);

  useEffect(() => {
    const mapData = GameMap();
    debug("map generated", mapData.timings);

    let newGameState = {
      ...gameState,
      map: mapData
    };
    newGameState.political = Political.init(mapData);
    const { city, territory, crossedBorder } = Political.atStep(mapData, newGameState.political, newGameState.step);
    newGameState.party.setPoliticalContext(city, territory, crossedBorder);
    newGameState.terrain = new TerrainTracker(mapData.features);
    newGameState.calendar.setEndDate(gracePeriod + mapData.features.length);
    newGameState.party.setCallbacks({
      onResourceChanged: onResourceChangedWithLog,
      onHistoryChanged: (res, oldValue, newValue, context) =>
        onHistoryChanged(res, oldValue, newValue, setEvent, context),
      onHistoryActionsChanged: (action, total, successes, failures, context) =>
        onHistoryActionsChanged(action, total, successes, failures, setEvent, context),
      onLevelChanged: (path, level, context) => onLevelChanged(path, level, context, setEvent)
    });
    newGameState.calendar.setCallbacks([
      (daysElapsed, context) => onDayChange(daysElapsed, context, setEvent),
      (daysElapsed, context) => handleDayChange(daysElapsed, context, setEvent)
    ]);

    newGameState.party.init();
    debug(Admin().startLocation, Locations.City.index, newGameState.party.city);
    if (Admin().startLocation === Locations.City.index) {
      newGameState.location = newGameState.party.city;
    }

    setGameState(newGameState);
  }, []);

  const resetLocation = useCallback(
    step => {
      const { city, territory, crossedBorder } = Political.atStep(gameState.map, gameState.political, step);

      gameState.terrain.atStep(step);
      const newGameState = { ...gameState };
      newGameState.party.setPoliticalContext(city, territory, crossedBorder);

      setGameState(newGameState);
    },
    [gameState]
  );

  const addNotification = useCallback(text => {
    setGameState(prev => ({
      ...prev,
      notifications: [text, ...prev.notifications]
    }));
  }, []);

  /**
   * Every health change, wrapped so a crossing into a worse band gets said once.
   *
   * The log is the only narrator this game has, and the page going quiet and dark is a state
   * rather than an announcement — a player who was looking at the map when it happened would
   * never learn *when*. So the visual carries the condition and this carries the moment.
   *
   * It sits here rather than in EventManager or phases.resourceListener because those two are
   * handed setEvent, and this is not an event: it is a line in the log, and addNotification is
   * only in scope in this file. See health.js for the bands and for what is worth saying.
   */
  const onResourceChangedWithLog = useCallback(
    (index, oldValue, newValue, context) => {
      onResourceChanged(index, oldValue, newValue, setEvent, context);

      if (index !== ResourceTypes.Health) return;

      const { peak } = context.party.resources.attribs.health;
      const line = healthCrossing(healthBand({ value: oldValue, peak }), healthBand({ value: newValue, peak }), newValue);
      if (line) addNotification(line);
    },
    [addNotification, setEvent]
  );

  // A run from the beginning: a fresh party and calendar back at step 0. Mirrors the mount
  // effect, including party.init() — without it the new party's resource values are undefined
  // (Health() supplies no `value`), so the player would respawn already dead.
  const startNewGame = (notifications = []) => {
    const newGameState = {
      ...gameState,
      party: new Party(startPhase),
      calendar: new Calendar(),
      message: '',
      position: 0,
      eventQueue: [],
      currentEvent: null,
      notifications,
      step: 0,
      lastStep: 0
    };
    newGameState.party.setCallbacks({
      onResourceChanged: onResourceChangedWithLog,
      onHistoryChanged: (res, oldValue, newValue, context) =>
        onHistoryChanged(res, oldValue, newValue, setEvent, context),
      onHistoryActionsChanged: (action, total, successes, failures, context) =>
        onHistoryActionsChanged(action, total, successes, failures, setEvent, context),
      onLevelChanged: (path, level, context) => onLevelChanged(path, level, context, setEvent)
    });
    newGameState.calendar.setCallbacks([
      (daysElapsed, context) => onDayChange(daysElapsed, context, setEvent),
      (daysElapsed, context) => handleDayChange(daysElapsed, context, setEvent)
    ]);
    newGameState.calendar.setEndDate(gracePeriod + gameState.map.features.length);

    resetEvents();
    resetAmbient();
    // The new party's progression is back at level zero, so the daily benefits its levels
    // bought are gone with it — but ResourceController is a singleton and does not know a run
    // ended. Clear it, then let the towns put back what they still owe: their standings live on
    // `political`, which survives the run.
    ResourceController.instance().resetDailyEffects();
    gameState.political.restoreControl();
    newGameState.party.init();

    const { city, territory, crossedBorder } = Political.atStep(gameState.map, gameState.political, 0);
    newGameState.party.setPoliticalContext(city, territory, crossedBorder);
    gameState.terrain.atStep(0);

    clock.resume();
    setGameState(newGameState);
  };

  const reset = () => startNewGame();

  const onDeath = () => startNewGame(['the road claims another']);

  // Provide gameState and functions to modify it to the context
  const value = {
    gameState,
    setGameState,
    setEvent,
    addNotification,
    reset,
    resetLocation,
    onDeath
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};

const useGame = () => useContext(GameContext);

export { GameProvider, useGame };
